import { MediaRoom, MediaUserRole } from '@/services/liveMedia.service';

// Agora stat snapshots every 2s, drained by collectHeartbeatQos for the 30s
// rollup. Own module so heartbeat actions (start / drain) and the
// stopEventHeartbeat mutation (stop) share it without a circular import.

export type AgoraNetworkQuality = { uplinkNetworkQuality?: number; downlinkNetworkQuality?: number };

export type AgoraSample = {
  ts: number;
  localAudio: Record<string, any> | null;
  localVideo: Record<string, any> | null;
  screenVideo: Record<string, any> | null;
  remoteVideo: Record<string, Record<string, any>> | null;
  remoteAudio: Record<string, Record<string, any>> | null;
  nq: AgoraNetworkQuality | null | undefined;
};

export const AGORA_SAMPLE_INTERVAL_MS = 2000;
// 30s heartbeat / 2s sampling = 15 samples; cap a little above so a late tick
// never drops data but a stalled heartbeat cannot grow the buffer unbounded.
export const AGORA_SAMPLE_BUFFER_CAP = 20;

let samples: AgoraSample[] = [];
let samplerIntervalId: ReturnType<typeof setInterval> | null = null;

export function stopAgoraSampler(): void {
  if (samplerIntervalId !== null) {
    clearInterval(samplerIntervalId);
    samplerIntervalId = null;
  }
  samples = [];
}

/**
 * Starts the 2s sampler, replacing any running one. Role and connection are
 * checked per tick so a user promoted to host mid-session is sampled without
 * a restart; non-hosts contribute nothing.
 */
export function startAgoraSampler(
  getRoom: () => MediaRoom | null | undefined,
  getNetworkQuality: () => AgoraNetworkQuality | null | undefined
): void {
  stopAgoraSampler();

  samplerIntervalId = setInterval(() => {
    const room = getRoom();
    if (!room?.client || room.userRole !== MediaUserRole.host) return;
    if (room.client.connectionState !== 'CONNECTED') return;

    const isScreenConn = room.screenShareClient?.connectionState === 'CONNECTED';
    samples.push({
      ts: Date.now(),
      localAudio: room.client.getLocalAudioStats() ?? null,
      localVideo: room.client.getLocalVideoStats() ?? null,
      screenVideo: isScreenConn ? room.screenShareClient.getLocalVideoStats() : null,
      remoteVideo: (room.client.getRemoteVideoStats() as Record<string, Record<string, any>>) ?? null,
      remoteAudio: (room.client.getRemoteAudioStats() as Record<string, Record<string, any>>) ?? null,
      nq: getNetworkQuality() ?? null
    });
    if (samples.length > AGORA_SAMPLE_BUFFER_CAP) samples.splice(0, samples.length - AGORA_SAMPLE_BUFFER_CAP);
  }, AGORA_SAMPLE_INTERVAL_MS);
}

export function hasAgoraSamples(): boolean {
  return samples.length > 0;
}

/** Returns all buffered samples and clears the buffer atomically. */
export function drainAgoraSamples(): AgoraSample[] {
  return samples.splice(0);
}
